import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  FiActivity,
  FiArrowLeft,
  FiAward,
  FiBarChart2,
  FiCheckCircle,
  FiTrendingUp,
  FiUsers,
} from 'react-icons/fi';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Flag from 'react-world-flags';
import { CoinsIcon } from '../../assets/icons/CoinsIcon';
import Avatar from '../../components/Avatar';
import ExpertFollowButton from '../../components/ExpertFollowButton';
import ExpertPaidTierCard from '../../components/expertComponents/ExpertPaidTierCard';
import ExpertPickPanel from '../../components/ExpertPickPanel';
import { ROUTES } from '../../constants';
import { avatarConfigDisplayColors } from '../../constants/avatarPalette';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../contexts/TranslationContext';
import { followApi } from '../../services/followService';
import { BetRecommendation, SimpleStats, tipsterApi } from '../../services/tipsterService';
import '../../styles/expert-profile.scss';
import { TipsterPublic, TipsterTierPublic } from '../../types/interfaces';

type Tab = 'current' | 'resolved';

const PAGE_SIZE = 25;

function getInitials(name: string | null): string {
  if (!name) return '?';
  return name.split(' ').filter(Boolean).map(w => w[0]).join('').slice(0, 2).toUpperCase();
}

const ExpertProfile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const [tipster, setTipster] = useState<TipsterPublic | null>(null);
  const [recommendations, setRecommendations] = useState<BetRecommendation[]>([]);
  const [stats, setStats] = useState<SimpleStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>('current');
  const [resolvedPage, setResolvedPage] = useState(1);
  const [paidTiers, setPaidTiers] = useState<TipsterTierPublic[]>([]);
  const [activeAccess, setActiveAccess] = useState<{ has_access: boolean; tier_name: string | null } | null>(null);
  const [searchParams] = useSearchParams();
  const justPaid = searchParams.get('subscribed') === '1';

  useEffect(() => {
    if (!id) return;
    const tipsterId = parseInt(id);
    Promise.all([
      tipsterApi.getById(tipsterId),
      tipsterApi.getRecommendations(tipsterId),
      tipsterApi.getSimpleStats(tipsterId).catch(() => null as SimpleStats | null),
      followApi.getFollowedIds().catch(() => [] as number[]),
      tipsterApi.getPaidTiers(tipsterId).catch(() => [] as TipsterTierPublic[]),
    ])
      .then(([tipsterData, recsData, statsData, followedIds, tiersData]) => {
        setTipster(tipsterData);
        setRecommendations(recsData);
        setStats(statsData);
        setIsFollowing(followedIds.includes(tipsterId));
        setPaidTiers(tiersData.filter(t => t.level > 0));

        if (tipsterData.stripe_onboarding_complete && tipsterData.has_paid_plan) {
          tipsterApi.checkExpertAccess(tipsterId)
            .then(setActiveAccess)
            .catch(() => {});
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!id || !justPaid || activeAccess?.has_access) return;
    const tipsterId = parseInt(id);
    let attempts = 0;
    const timer = setInterval(() => {
      attempts += 1;
      tipsterApi.checkExpertAccess(tipsterId)
        .then(res => {
          setActiveAccess(res);
          if (res.has_access || attempts >= 5) clearInterval(timer);
        })
        .catch(() => { if (attempts >= 5) clearInterval(timer); });
    }, 2000);
    return () => clearInterval(timer);
  }, [id, justPaid, activeAccess?.has_access]);

  const isUnresolved = (result?: string) =>
    !result || result === 'TO_RESOLVE' || result === 'UNKNOWN';

  const ongoingPicks = recommendations.filter(rec => {
    const isPast = rec.bet_event?.game?.datetime
      ? new Date(rec.bet_event.game.datetime) <= new Date()
      : false;
    return isUnresolved(rec.bet_event?.result) && isPast;
  });

  const upcomingPicks = recommendations.filter(rec => {
    const isFuture = rec.bet_event?.game?.datetime
      ? new Date(rec.bet_event.game.datetime) > new Date()
      : true;
    return isUnresolved(rec.bet_event?.result) && isFuture;
  });

  const resolvedPicks = recommendations.filter(rec => {
    const result = rec.bet_event?.result;
    const isPast = rec.bet_event?.game?.datetime
      ? new Date(rec.bet_event.game.datetime) <= new Date()
      : false;
    return result && !isUnresolved(result) && isPast;
  });
  const resolvedTotalPages = Math.max(1, Math.ceil(resolvedPicks.length / PAGE_SIZE));
  const pagedResolvedPicks = resolvedPicks.slice((resolvedPage - 1) * PAGE_SIZE, resolvedPage * PAGE_SIZE);

  const handleToggleFollow = async () => {
    if (!tipster || followLoading) return;
    if (!isAuthenticated) {
      navigate(
        `${ROUTES.LOGIN}?redirect=${encodeURIComponent(id ? `/experts/${id}` : ROUTES.EXPERTS_SEARCH)}`
      );
      return;
    }
    setFollowLoading(true);
    try {
      if (isFollowing) {
        await followApi.unfollow(tipster.id);
        setIsFollowing(false);
        setTipster(p => p ? { ...p, followers_count: p.followers_count - 1 } : p);
      } else {
        await followApi.follow(tipster.id);
        setIsFollowing(true);
        setTipster(p => p ? { ...p, followers_count: p.followers_count + 1 } : p);
      }
    } catch { /* silently */ } finally {
      setFollowLoading(false);
    }
  };

  if (loading) return <div className="expert-profile">{t.common.loading}</div>;
  if (!tipster) return <div className="expert-profile">{t.common.expertNotFound}</div>;

  const tags = [tipster.tag_1, tipster.tag_2, tipster.tag_3].filter(Boolean) as string[];
  const currentCount = ongoingPicks.length + upcomingPicks.length;

  const winRate =
    stats && stats.total_picks > 0
      ? `${((stats.total_picks_won / stats.total_picks) * 100).toFixed(1)}%`
      : '—';

  const roiNum = stats?.roi ?? null;
  const roiStr =
    roiNum !== null ? `${roiNum >= 0 ? '+' : ''}${roiNum.toFixed(1)}%` : '—';

  const statItems = [
    { icon: <FiActivity size={11} />, label: t.expertProfile.winRate, value: winRate, mod: 'highlight' as const },
    {
      icon: <FiTrendingUp size={11} />,
      label: t.common.roi,
      value: roiStr,
      mod: roiNum !== null ? (roiNum >= 0 ? 'win' : 'loss') as 'win' | 'loss' : undefined,
    },
    { icon: <FiAward size={11} />, label: t.expertProfile.picks, value: String(stats?.total_picks ?? '—'), mod: undefined },
    { icon: <FiUsers size={11} />, label: t.expertProfile.followers, value: String(tipster.followers_count), mod: undefined },
  ];

  const hasAvatar =
    tipster.avatar_matrix && tipster.avatar_matrix.length > 0 && tipster.avatar_config;

  const hasPaidPlan = Boolean(tipster.stripe_onboarding_complete && tipster.has_paid_plan);
  const userHasAccess = Boolean(activeAccess?.has_access);
  const showBuyAccess = hasPaidPlan && !userHasAccess;
  const activeTierColor = paidTiers.find(t => t.name === activeAccess?.tier_name)?.color ?? null;

  const expertName = tipster?.full_name ?? 'Expert Tipster';
  const expertDesc = tipster?.description
    ? tipster.description.slice(0, 155)
    : `Follow ${expertName}'s sports betting picks and predictions on PhilipSnat.`;

  return (
    <div className="expert-profile">
      <Helmet>
        <title>{expertName} | Expert Tipster on PhilipSnat</title>
        <meta name="description" content={expertDesc} />
        <meta property="og:title" content={`${expertName} | Expert Tipster on PhilipSnat`} />
        <meta property="og:description" content={expertDesc} />
        <meta property="og:url" content={`https://philipsnat.com/experts/${id}`} />
        <link rel="canonical" href={`https://philipsnat.com/experts/${id}`} />
      </Helmet>
      <button
        className="expert-profile__back"
        onClick={() => navigate(-1)}
        aria-label={t.common.back}
      >
        <FiArrowLeft size={14} /> {t.common.back}
      </button>

      {justPaid && (
        <div className={`expert-profile__pay-banner${userHasAccess ? ' expert-profile__pay-banner--active' : ''}`}>
          <FiCheckCircle size={16} />
          <span>{userHasAccess ? t.expertProfile.accessActiveBanner : t.expertProfile.accessPendingBanner}</span>
        </div>
      )}

      <div className="expert-profile__layout">
        {/* ── Sidebar / mobile header ── */}
        <aside className="expert-profile__sidebar">
          <div className="expert-profile__identity-card">
            <div className="expert-profile__avatar-wrap">
              {hasAvatar ? (
                <Avatar
                  matrix={tipster.avatar_matrix!}
                  {...avatarConfigDisplayColors(tipster.avatar_config!)}
                  compact
                />
              ) : (
                <div className="expert-profile__avatar-initials">
                  {getInitials(tipster.full_name)}
                </div>
              )}
            </div>

            <div className="expert-profile__identity-info">

              <div className="expert-profile__identity-header">
                <div className="expert-profile__name-row">
                  <h1 className="expert-profile__name">
                    {tipster.full_name || t.common.expertNumber.replace('{id}', String(tipster.id))}
                  </h1>
                  {tipster.is_verified && (
                    <FiCheckCircle size={16} className="expert-profile__verified" />
                  )}
                </div>

                {tipster.country && (
                <div className="expert-profile__meta">
                    <div className="expert-profile__flag-wrap">
                      <Flag code={tipster.country} className="expert-profile__flag" />
                    </div>
                    <div className="expert-profile__country-text">{tipster.country}</div>
                </div>
              )}
              </div>

              {tags.length > 0 && (
                <div className="expert-profile__tags">
                  {tags.map(t => (
                    <span key={t} className="expert-profile__tag">{t}</span>
                  ))}
                </div>
              )}
            </div>

            {/* Visible only on desktop (column layout) */}
            <div className="expert-profile__card-divider" />

            <div className="expert-profile__action-btns">
              <ExpertFollowButton
                isFollowing={isFollowing}
                followersCount={tipster.followers_count}
                disabled={followLoading}
                onClick={handleToggleFollow}
              />
              {showBuyAccess && (
                <button
                  type="button"
                  className="expert-profile__buy-access-btn"
                  onClick={() => navigate(`/experts/${id}/buy`)}
                >
                  <CoinsIcon className="expert-profile__buy-access-icon" />
                  <span>{t.expertProfile.buyAccess}</span>
                </button>
              )}
              <button
                type="button"
                className="expert-profile__stats-btn"
                onClick={() => navigate(`/experts/${id}/stats`)}
              >
                <FiBarChart2 size={14} /> {t.expertProfile.fullStats}
              </button>
            </div>
          </div>

          {userHasAccess && (
            <div
              className="expert-profile__access-badge"
              style={activeTierColor ? {
                color: activeTierColor,
                borderColor: `color-mix(in srgb, ${activeTierColor} 35%, transparent)`,
                background: `color-mix(in srgb, ${activeTierColor} 12%, transparent)`,
              } : undefined}
            >
              <FiCheckCircle size={13} />
              <span>{activeAccess?.tier_name ?? 'Active Plan'}</span>
            </div>
          )}

          <div className="expert-profile__stats-grid">
            {statItems.map(s => (
              <div key={s.label} className="expert-profile__stat-card">
                <span className="expert-profile__stat-label">
                  <span className="expert-profile__stat-icon">{s.icon}</span>{s.label}
                </span>
                <span
                  className={`expert-profile__stat-value${s.mod ? ` expert-profile__stat-value--${s.mod}` : ''}`}
                >
                  {s.value}
                </span>
              </div>
            ))}
          </div>
        </aside>

        {/* ── Main content ── */}
        <main className="expert-profile__main">
          {tipster.description && (
            <div className="expert-profile__description-block">
              <p className="expert-profile__description">{tipster.description}</p>
            </div>
          )}

          {paidTiers.length > 0 && !userHasAccess && (
            <div className="expert-profile__plans">
              {paidTiers.map((tier) => (
                <ExpertPaidTierCard
                  key={tier.id}
                  name={tier.name}
                  priceMonthly={
                    tier.price_monthly != null ? Number(tier.price_monthly) : null
                  }
                  featuresDescription={tier.features_description}
                  featuresList={tier.features_list}
                  featured={tier.featured}
                  color={tier.color}
                  activePicksCount={tier.active_picks_count}
                  onClick={showBuyAccess ? () => navigate(`/experts/${id}/buy`) : undefined}
                />
              ))}
            </div>
          )}

          <div className="expert-profile__tabs">
            <button
              className={`expert-profile__tab${activeTab === 'current' ? ' expert-profile__tab--active' : ''}`}
              onClick={() => setActiveTab('current')}
            >
              {t.expertProfile.currentPicks}
              {currentCount > 0 && (
                <span className="expert-profile__tab-count">{currentCount}</span>
              )}
            </button>
            <button
              className={`expert-profile__tab${activeTab === 'resolved' ? ' expert-profile__tab--active' : ''}`}
              onClick={() => { setActiveTab('resolved'); setResolvedPage(1); }}
            >
              {t.expertProfile.resolved}
              {resolvedPicks.length > 0 && (
                <span className="expert-profile__tab-count">{resolvedPicks.length}</span>
              )}
            </button>
          </div>

          <div className="expert-profile__picks-list">
            {activeTab === 'current' ? (
              currentCount === 0 ? (
                <p className="expert-profile__picks-empty">{t.expertProfile.noActivePicks}</p>
              ) : (
                <>
                  {ongoingPicks.map(rec => (
                    <ExpertPickPanel key={rec.id} recommendation={rec} ongoing />
                  ))}
                  {upcomingPicks.map(rec => (
                    <ExpertPickPanel key={rec.id} recommendation={rec} />
                  ))}
                </>
              )
            ) : resolvedPicks.length === 0 ? (
              <p className="expert-profile__picks-empty">{t.expertProfile.noResolvedPicks}</p>
            ) : (
              <>
                {pagedResolvedPicks.map(rec => (
                  <ExpertPickPanel key={rec.id} recommendation={rec} resolved />
                ))}
                {resolvedTotalPages > 1 && (
                  <div className="expert-profile__pagination">
                    <button
                      type="button"
                      className="button_secondary"
                      disabled={resolvedPage <= 1}
                      onClick={() => setResolvedPage(p => p - 1)}
                    >
                      {t.common.previous}
                    </button>
                    <span className="expert-profile__pagination-info">
                      {t.common.paginationShort
                        .replace('{page}', String(resolvedPage))
                        .replace('{totalPages}', String(resolvedTotalPages))}
                    </span>
                    <button
                      type="button"
                      className="button_secondary"
                      disabled={resolvedPage >= resolvedTotalPages}
                      onClick={() => setResolvedPage(p => p + 1)}
                    >
                      {t.common.next}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default ExpertProfile;
